// Shared donation generators for mock donor personas.

export type MockDonation = {
  id: string
  state: string
  charity: string
  amount: number
  giftaid: number
  time: string
  source: string
  sourceCategory: 'online' | 'contactless' | 'swiftaid' | 'manual'
}

export type YearSpec = {
  period: string
  count: number
  /** One large gift (> £3,200) in this year when set */
  bigGiftPence?: number
}

export type FinancialYearPayload = {
  period: string
  totalDonations: number
  totalGiftAid: number
  donations: MockDonation[]
}

const CHARITIES = [
  'RNLI',
  'British Heart Foundation',
  'Cancer Research UK',
  'Royal British Legion',
  'NSPCC',
  'Dogs Trust',
  'Macmillan Cancer Support',
  'Shelter',
  'Oxfam',
  'Mind',
  'WaterAid',
  'British Red Cross',
  'RSPB',
  'Crisis',
  'Great Ormond Street Hospital Charity',
  'The Royal Society for the Prevention of Cruelty to Animals (RSPCA)',
]

const SMALL_AMOUNTS = [1000, 1500, 2000, 2500, 3000, 3500, 10000]

const STATES = [
  'received',
  'declared',
  'settled',
  'pending',
  'cancelled',
  'declaredCancelling',
] as const

const ONLINE_SOURCES = ['JustGiving', 'Give as you Live', 'Enthuse'] as const

const ga = (pence: number) => Math.round(pence * 0.25)

/** Mock “today” ceiling for donation timestamps (Monday 6 July 2026, end of UTC day). */
export const MOCK_DONATION_LATEST_MS = Date.UTC(2026, 6, 6, 23, 59, 59, 999)

export function clampDonationTimeMs(ms: number): number {
  return Math.min(ms, MOCK_DONATION_LATEST_MS)
}

export function clampDonationIso(iso: string): string {
  return new Date(clampDonationTimeMs(new Date(iso).getTime())).toISOString()
}

function taxYearIso(period: string, index: number, total: number): string {
  const startYear = parseInt(period.split('-')[0], 10)
  const start = Date.UTC(startYear, 3, 6)
  const taxYearEnd = Date.UTC(startYear + 1, 3, 5, 12, 0, 0)
  const end = clampDonationTimeMs(taxYearEnd)
  const ratio = total <= 1 ? 0 : index / (total - 1)
  return new Date(start + (end - start) * ratio).toISOString()
}

function pickSource(index: number): {
  sourceCategory: MockDonation['sourceCategory']
  source: string
} {
  const roll = index % 10
  if (roll <= 3) {
    return { sourceCategory: 'online', source: ONLINE_SOURCES[index % ONLINE_SOURCES.length] }
  }
  if (roll <= 5) {
    return { sourceCategory: 'contactless', source: 'Contactless' }
  }
  return {
    sourceCategory: 'swiftaid',
    source: 'Swiftaid',
  }
}

export function buildDonationsForYear(spec: YearSpec): MockDonation[] {
  if (spec.count === 0) return []

  const donations: MockDonation[] = []
  const bigSlot = spec.bigGiftPence ? Math.floor(spec.count / 2) : -1

  for (let i = 0; i < spec.count; i += 1) {
    const { sourceCategory, source } = pickSource(i)
    const amount =
      i === bigSlot && spec.bigGiftPence
        ? spec.bigGiftPence
        : SMALL_AMOUNTS[i % SMALL_AMOUNTS.length]

    donations.push({
      id: `don-${spec.period}-${String(i + 1).padStart(2, '0')}`,
      state: STATES[i % STATES.length],
      charity: CHARITIES[i % CHARITIES.length],
      amount,
      giftaid: ga(amount),
      time: taxYearIso(spec.period, i, spec.count),
      sourceCategory,
      source,
    })
  }

  return donations
}

export function buildDonationsInWindow(options: {
  period: string
  count: number
  startMs: number
  endMs: number
  idPrefix: string
  /** When set, every donation uses this online source label */
  sourceLabel?: string
}): MockDonation[] {
  const { period, count, startMs, endMs, idPrefix, sourceLabel } = options
  if (count === 0) return []

  const donations: MockDonation[] = []
  const cappedEndMs = clampDonationTimeMs(endMs)
  const cappedStartMs = Math.min(startMs, cappedEndMs)
  const span = Math.max(cappedEndMs - cappedStartMs, 1)

  for (let i = 0; i < count; i += 1) {
    const ratio = count <= 1 ? 0 : i / (count - 1)
    const amount = SMALL_AMOUNTS[i % SMALL_AMOUNTS.length]
    const source = sourceLabel ?? pickSource(i).source
    const sourceCategory = sourceLabel ? 'online' : pickSource(i).sourceCategory

    donations.push({
      id: `${idPrefix}-${String(i + 1).padStart(2, '0')}`,
      state: STATES[i % STATES.length],
      charity: CHARITIES[i % CHARITIES.length],
      amount,
      giftaid: ga(amount),
      time: new Date(cappedStartMs + span * ratio).toISOString(),
      sourceCategory,
      source,
    })
  }

  return donations
}

export function toFinancialYear(period: string, donations: MockDonation[]): FinancialYearPayload {
  return {
    period,
    totalDonations: donations.reduce((sum, row) => sum + row.amount, 0),
    totalGiftAid: donations.reduce((sum, row) => sum + row.giftaid, 0),
    donations,
  }
}

export function buildFinancialYearsFromSpecs(specs: YearSpec[]): FinancialYearPayload[] {
  return specs.map((spec) => toFinancialYear(spec.period, buildDonationsForYear(spec)))
}
